import { useRef, useState } from "react";
import { Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Turnstile, type TurnstileHandle } from "@/components/Turnstile";
import { forgotPassword } from "@/lib/auth";
import logo from "@/assets/Logo.png";
import bgDecorative from "@/assets/bg-decorative.svg";

const forgotPasswordSchema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email"),
});

type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;

function ForgotPassword() {
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);

  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  async function onSubmit(values: ForgotPasswordValues) {
    setServerError(null);

    if (!turnstileToken) {
      setServerError("Please complete the verification challenge.");
      return;
    }

    try {
      await forgotPassword({ ...values, turnstileToken });
      setSubmitted(true);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "Something went wrong");
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-4 py-8"
      style={{
        backgroundImage: `url(${bgDecorative})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full max-w-[560px] overflow-hidden rounded-3xl border border-slate-200 bg-card shadow-xl">
        <div className="flex flex-col items-center gap-8 p-16">
          <img className="h-9 w-24 object-contain" src={logo} alt="180Pi" />

          {submitted ? (
            <>
              <div className="flex w-full flex-col items-center gap-2">
                <h1 className="text-center text-3xl font-extrabold leading-9 text-primary">Check your email</h1>
                <p className="text-center text-base font-normal leading-6 text-muted-foreground">
                  If an account exists for that email, we've sent a link to reset your password.
                </p>
              </div>
              <Link href="/login" className="text-sm text-primary underline-offset-4 hover:underline">
                Back to log in
              </Link>
            </>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col items-center gap-8">
                <div className="flex w-full flex-col items-center gap-2">
                  <h1 className="text-center text-3xl font-extrabold leading-9 text-primary">Forgot password?</h1>
                  <p className="text-center text-base font-normal leading-6 text-muted-foreground">
                    Enter your email and we'll send you a link to reset it
                  </p>
                </div>

                <div className="flex w-full flex-col gap-5">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-semibold leading-5 text-slate-700">Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            autoComplete="email"
                            placeholder="name@company.com"
                            className="h-auto rounded-2xl border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-1 focus-visible:ring-slate-300"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Turnstile
                    ref={turnstileRef}
                    onVerify={setTurnstileToken}
                    onExpire={() => setTurnstileToken(null)}
                  />

                  {serverError && <p className="text-sm font-medium text-destructive">{serverError}</p>}

                  <Button
                    type="submit"
                    disabled={form.formState.isSubmitting || !turnstileToken}
                    className="w-full rounded-2xl py-3 text-sm font-bold shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]"
                  >
                    {form.formState.isSubmitting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      "Send reset link"
                    )}
                  </Button>
                </div>

                <p className="text-center text-sm text-muted-foreground">
                  Remembered your password?{" "}
                  <Link href="/login" className="text-primary underline-offset-4 hover:underline">
                    Log in
                  </Link>
                </p>
              </form>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
